import type { QueueDiagnostics } from "../queue";

export type UploadTransportResult = { success: true } | { success: false; error?: string };

export type UploadCompletionResult = { success: true } | { success: false; error: string };

export type AudioUploadState = {
  uploadProgressPercent: number | null;
  isUploading: boolean;
  errorMessage: string | null;
};

export type AudioRecordingRef = {
  audioRecordingId: string;
};

export enum AudioUploadFailureStage {
  PrepareFailed = "prepare_failed",
  MarkUploadingFailed = "mark_uploading_failed",
  TransportFailed = "transport_failed",
  MarkCompletedFailed = "mark_completed_failed",
}

/**
 * Structured debug info attached to upload failures.
 *
 * Enum-valued fields (`artifactFailureReason`, `artifactState`, `removeReason`) are typed as
 * `string` so this shared package stays agnostic to the desktop-specific enums. Values
 * correspond to `$/types.DesktopAudioArtifactFailureReason`,
 * `DesktopAudioArtifactState`, and `DesktopAudioArtifactRemoveReason` respectively.
 */
export type AudioUploadFailureDebugInfo = {
  artifactFailureReason?: string;
  artifactFilePath?: string;
  manifestPath?: string;
  artifactDirectoryPath?: string;
  artifactState?: string;
  artifactSizeBytes?: number;
  appendCount?: number;
  appendFailureCount?: number;
  bytesWritten?: number;
  createdAt?: string;
  updatedAt?: string;
  firstAppendAt?: string;
  lastAppendAt?: string;
  finalizedAt?: string;
  artifactRefPresent?: boolean;
  artifactSnapshotPresent?: boolean;
  removeReason?: string;
  removedAt?: string;
  diagnosticErrorMessage?: string;
};

export type AudioUploadErrorRef = AudioRecordingRef & {
  errorMessage: string;
  uploadStage?: AudioUploadFailureStage;
  forceRestart?: boolean;
  debugInfo?: AudioUploadFailureDebugInfo;
};

export type AudioUploadStartRef = AudioRecordingRef & {
  forceRestart?: boolean;
};

export type AudioUploadProgressUpdate = AudioRecordingRef & {
  state: AudioUploadState;
};

export type AudioUploadJob = {
  audioRecordingId: string;
  forceRestart?: boolean;
};

export type AudioUploadMeta = {
  capturedAudioId: string;
  associatedAudioRecordingId: string;
  associatedNoteId?: string | null;
  associatedSessionId?: string | null;
  transcriptText: string;
  transcriptArray: unknown[];
  preferredMimeType?: string;
};

export type PreparedAudioUploadPayload<TSourceRef> = {
  source: TSourceRef;
  uploadMeta: AudioUploadMeta;
};

export type PreparedAudioUploadResult<TSourceRef> =
  | { success: true; payload: PreparedAudioUploadPayload<TSourceRef> }
  | {
      success: false;
      error: string;
      shouldPersistError?: boolean;
      uploadStage?: AudioUploadFailureStage;
      debugInfo?: AudioUploadFailureDebugInfo;
    };

export type AudioUploadTransportInput<TSourceRef> = Pick<
  PreparedAudioUploadPayload<TSourceRef>,
  "source" | "uploadMeta"
> & {
  forceRestart?: boolean;
  onProgress: (args: { progressPercent: number }) => void;
};

export interface AudioUploadPayloadProvider<TSourceRef> {
  prepareUploadPayload(args: AudioRecordingRef): Promise<PreparedAudioUploadResult<TSourceRef>>;
}

export type AudioUploadPrepareUploadPayload<TSourceRef> =
  AudioUploadPayloadProvider<TSourceRef>["prepareUploadPayload"];

export interface AudioUploadTransport<TSourceRef> {
  upload(args: AudioUploadTransportInput<TSourceRef>): Promise<UploadTransportResult>;
}

export type AudioUploadTransportUpload<TSourceRef> = AudioUploadTransport<TSourceRef>["upload"];

export interface AudioUploadPersistence {
  markAsUploading(args: AudioUploadStartRef): Promise<void>;
  markAsUploadCompleted(args: AudioRecordingRef): Promise<void>;
  markAsUploadErrored(args: AudioUploadErrorRef): Promise<void>;
}

export interface AudioUploadProgressTracker {
  setProgress(args: AudioUploadProgressUpdate): void;
  clearProgress(args: AudioRecordingRef): void;
}

export type AudioUploadProgressSetProgressArgs = AudioUploadProgressUpdate;

export type AudioUploadProgressClearProgressArgs = AudioRecordingRef;

export type AudioUploadEnqueueArgs = {
  job: AudioUploadJob;
};

export type AudioUploadCompleteCallbackArgs = {
  job: AudioUploadJob;
};

export type AudioUploadErrorCallbackArgs = {
  job: AudioUploadJob;
  error: string;
};

export enum AudioUploadDiagnosticEventType {
  PrepareStarted = "prepare_started",
  PrepareSucceeded = "prepare_succeeded",
  PrepareFailed = "prepare_failed",
  MarkUploadingFailed = "mark_uploading_failed",
  TransportStarted = "transport_started",
  TransportSucceeded = "transport_succeeded",
  TransportFailed = "transport_failed",
  MarkCompletedFailed = "mark_completed_failed",
  FinishWithError = "finish_with_error",
  ErrorPersistenceFailed = "error_persistence_failed",
  ProgressSetFailed = "progress_set_failed",
  ProgressClearFailed = "progress_clear_failed",
}

export type AudioUploadDiagnosticEvent =
  | {
      type: AudioUploadDiagnosticEventType.PrepareStarted;
      audioRecordingId: string;
      forceRestart: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.PrepareSucceeded;
      audioRecordingId: string;
      forceRestart: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.PrepareFailed;
      audioRecordingId: string;
      forceRestart: boolean;
      errorMessage: string;
      shouldPersistError: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.MarkUploadingFailed;
      audioRecordingId: string;
      forceRestart: boolean;
      errorMessage: string;
    }
  | {
      type: AudioUploadDiagnosticEventType.TransportStarted;
      audioRecordingId: string;
      forceRestart: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.TransportSucceeded;
      audioRecordingId: string;
      forceRestart: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.TransportFailed;
      audioRecordingId: string;
      forceRestart: boolean;
      errorMessage: string;
    }
  | {
      type: AudioUploadDiagnosticEventType.MarkCompletedFailed;
      audioRecordingId: string;
      forceRestart: boolean;
      errorMessage: string;
    }
  | {
      type: AudioUploadDiagnosticEventType.FinishWithError;
      audioRecordingId: string;
      forceRestart: boolean;
      errorMessage: string;
      shouldPersistError: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.ErrorPersistenceFailed;
      audioRecordingId: string;
      forceRestart: boolean;
      errorMessage: string;
      persistenceErrorMessage: string;
    }
  | {
      type: AudioUploadDiagnosticEventType.ProgressSetFailed;
      audioRecordingId: string;
      forceRestart: boolean;
    }
  | {
      type: AudioUploadDiagnosticEventType.ProgressClearFailed;
      audioRecordingId: string;
      forceRestart: boolean;
    };

export interface AudioUploadDiagnostics {
  onEvent?: (args: { event: AudioUploadDiagnosticEvent }) => void;
  queue?: QueueDiagnostics<AudioUploadJob>;
}

export interface AudioUploadManagerCallbacks {
  onUploadComplete?: (args: AudioUploadCompleteCallbackArgs) => void;
  onUploadError?: (args: AudioUploadErrorCallbackArgs) => void;
}

export interface AudioUploadController {
  enqueue(args: AudioUploadEnqueueArgs): void;
  resetState(): void;
  isActive(): boolean;
}

export type AudioUploadManagerHandlers<TSourceRef> = {
  payload: AudioUploadPayloadProvider<TSourceRef>;
  transport: AudioUploadTransport<TSourceRef>;
  persistence: AudioUploadPersistence;
  progress: AudioUploadProgressTracker;
};

export type AudioUploadManagerConfig<TSourceRef> = {
  handlers: AudioUploadManagerHandlers<TSourceRef>;
  callbacks?: AudioUploadManagerCallbacks;
  diagnostics?: AudioUploadDiagnostics;
};
